import React from 'react';

import { ContentClass, SearchFilterContentTypes } from '../../Shared/enums';
import { useConversationalSearch } from '../components/navSearch/NavSearchContext';
import SearchFilterOptions from '../entities/SearchFilterOptions';
import { useHydration } from './useHydration';

const AI_PLACEHOLDER = "Describe what you need... and we'll find what fits...";
const CONV_SEARCH_PATH = '/ai/conv-search/new';

interface UseSearchWithAIOptions {
  showConversationalSearchCta: boolean;
}

interface UseSearchWithAIResult {
  showAiCta: boolean;
  conversationalSearchEnabled: boolean;
  setConversationalSearchEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  getPlaceholder: (defaultPlaceholder: string) => string;
  handleConversationalRedirect: (
    searchTerm: string,
    searchOrigin: string
  ) => boolean;
  isSearchWithAIEnabledForContentType: (contentType: string) => boolean;
}

export const isSearchWithAIEnabledForContentType = (
  contentType: string
): boolean => {
  const contentClass = SearchFilterOptions.getContentClassFromContentType(
    contentType as SearchFilterContentTypes
  );

  // For now, we only support Search with AI for video content types
  return contentClass === ContentClass.Video;
};

export function useSearchWithAI({
  showConversationalSearchCta,
}: UseSearchWithAIOptions): UseSearchWithAIResult {
  const isHydrated = useHydration();
  const { conversationalSearchEnabled, setConversationalSearchEnabled } =
    useConversationalSearch();

  const showAiCta = isHydrated && showConversationalSearchCta;

  const getPlaceholder = (defaultPlaceholder: string): string =>
    conversationalSearchEnabled ? AI_PLACEHOLDER : defaultPlaceholder;

  const handleConversationalRedirect = (
    searchTerm: string,
    searchOrigin: string
  ): boolean => {
    if (!conversationalSearchEnabled) return false;

    const url = new URL(CONV_SEARCH_PATH, window.location.origin);
    const params = new URLSearchParams({
      search_origin: searchOrigin,
      prompt: searchTerm,
    });
    url.search = params.toString();
    window.location.href = url.toString();
    return true;
  };

  return {
    showAiCta,
    conversationalSearchEnabled,
    setConversationalSearchEnabled,
    getPlaceholder,
    handleConversationalRedirect,
    isSearchWithAIEnabledForContentType,
  };
}
